/**
 * Inheritance is strictly product default → inheriting variant.
 *
 * NULL on a variant inherits the product field.
 * A non-null variant value is an operator override and never writes back
 * to products.selling_price / cost_price / weight_kg / length_cm / width_cm /
 * height_cm, nor to sibling variants.
 *
 * Product defaults change only through an explicit product/default edit
 * (or an explicit "Update product base" operator choice). Matching variant
 * overrides are never averaged, min/maxed, or auto-promoted into the product.
 */

export type CatalogueDefaultField =
  | "selling_price"
  | "cost_price"
  | "weight_kg"
  | "length_cm"
  | "width_cm"
  | "height_cm";

export const CATALOGUE_DEFAULT_FIELDS: CatalogueDefaultField[] = [
  "selling_price",
  "cost_price",
  "weight_kg",
  "length_cm",
  "width_cm",
  "height_cm",
];

export type CatalogueDefaults = Record<CatalogueDefaultField, number | null>;

export function inheritFromProductDefault(
  stored: number | null,
  productDefault: number | null,
): number | null {
  return stored ?? productDefault;
}

/** Copies of the previous product default become inherit. Explicit overrides stay stored. */
export function storedVariantAfterBaseChange(opts: {
  oldBase: number | null;
  newBase: number | null;
  stored: number | null;
}): number | null {
  if (opts.stored == null) return null;
  if (opts.oldBase != null && opts.stored === opts.oldBase) return null;
  return opts.stored;
}

export function variantPricesAfterBaseChange(opts: {
  oldBase: number | null;
  newBase: number | null;
  stored: Array<number | null>;
}): Array<number | null> {
  return opts.stored.map((stored) => storedVariantAfterBaseChange({ ...opts, stored }));
}

/** Same inherit rule as selling price: copies of the old product cost are not overrides. */
export function variantCostsAfterBaseChange(opts: {
  oldBase: number | null;
  newBase: number | null;
  stored: Array<number | null>;
}): Array<number | null> {
  return variantPricesAfterBaseChange(opts);
}

export function variantStoredAfterProductDefaultChange(
  oldDefaults: CatalogueDefaults,
  newDefaults: CatalogueDefaults,
  stored: CatalogueDefaults,
): CatalogueDefaults {
  const next = { ...stored };
  for (const field of CATALOGUE_DEFAULT_FIELDS) {
    next[field] = storedVariantAfterBaseChange({
      oldBase: oldDefaults[field],
      newBase: newDefaults[field],
      stored: stored[field],
    });
  }
  return next;
}

export function effectiveCatalogueDefaults(
  product: CatalogueDefaults,
  stored: CatalogueDefaults,
): CatalogueDefaults {
  const next = { ...stored };
  for (const field of CATALOGUE_DEFAULT_FIELDS) {
    next[field] = inheritFromProductDefault(stored[field], product[field]);
  }
  return next;
}

export function shouldCollapseUniformOverrides(provenance: {
  kind: "all_identical_without_base_save" | "copies_of_previous_product_default" | "explicit_promote_to_base";
}): boolean {
  return provenance.kind !== "all_identical_without_base_save";
}

/** UI hint only. Never auto-write the product default from this result. */
export function uniformOverridePromotionCandidate(
  lines: Array<{ selling_price: number | null }>,
  productBase: number | null,
): { amount: number } | null {
  if (lines.length < 2) return null;
  const prices = lines.map((l) => l.selling_price);
  if (prices.some((p) => p == null)) return null;
  const amount = prices[0] as number;
  if (!prices.every((p) => p === amount)) return null;
  if (productBase != null && amount === productBase) return null;
  return { amount };
}

export function inheritedEffectivePrice(productBase: number | null, stored: number | null): number | null {
  return stored ?? productBase;
}
